import React from 'react';
import { graphql } from 'gatsby';
import { Layout, SliceZone } from '../components';
import {
  Wrapper,
  Image,
  HtmlContent,
  Shape,
} from '../components/common';
import {
  useMousePosition,
  useDimensions,
  useScrollPosition,
} from '../util/hooks';
import { isClient } from '../util/helpers';
import './about-us.scss';

const About = (props) => {
  const {
    data: {
      page: {
        data: pageData,
      },
      etchedIceCream,
      etchedRainbow,
    },
    location,
  } = props;
  const {
    pageName,
    headline,
    paragraphTop,
    paragraphBottom,
    imageTopLeft,
    imageRight,
    imageBottomLeft,
    metaTitle,
    metaDescription,
    openGraphImage,
    body,
  } = pageData;
  const seoData = {
    metaTitle,
    metaDescription,
    openGraphImage,
  };
  const {
    x: cursorX,
    y: cursorY,
  } = useMousePosition();
  const {
    viewportWidth,
    viewportHeight,
    documentHeight,
  } = useDimensions();
  const viewportTopPos = useScrollPosition();
  if (isClient) document.documentElement.style.setProperty('--bg-colour', '#FFFFF4');
  return (
    <Layout
      location={location}
      seoData={seoData}
      cursorX={cursorX}
      cursorY={cursorY}
      viewportWidth={viewportWidth}
      viewportHeight={viewportHeight}
      documentHeight={documentHeight}
      viewportTopPos={viewportTopPos}
    >
      <div className="about-us-page">
        <section className="about-intro">
          <div className="framing-shapes">
            <Shape
              shapeName="arc"
              shapeColour="#FF6C13"
            />
            <Shape
              shapeName="mountain"
              shapeColour="#2FCF00"
            />
            <Shape
              shapeName="vessel"
              shapeColour="#00BEE0"
            />
            <Shape
              shapeName="square"
              shapeColour="#FF5ABB"
            />
          </div>
          <Wrapper isBound>
            <span className="page-label">{pageName.text}</span>
            <h1 className="headline">{headline.text}</h1>
            <div className="section-top cols">
              <div className="col col-left">
                <div className="image-top-left">
                  <div className="image-container">
                    <Image image={imageTopLeft} />
                    <div className="illustration illustration-ice-cream">
                      <Image
                        image={{ localFile: etchedIceCream }}
                        className="contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col col-right">
                <HtmlContent
                  className="about-para paragraph-top"
                  content={paragraphTop.html}
                />
                <div className="image-right">
                  <div className="image-container">
                    <Image image={imageRight} />
                    <div className="illustration illustration-rainbow">
                      <Image
                        image={{ localFile: etchedRainbow }}
                        className="contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="section-bottom cols">
              <div className="col col-left">
                <div className="image-bottom-left">
                  <div className="image-container">
                    <Image image={imageBottomLeft} />
                    <div className="illustration illustration-rainbow-svg">
                      <svg viewBox="0 0 212 162" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" fillRule="evenodd">
                          <path d="M16.878 142.252c-10.005 8.834 36.15 26.822 40.448 6.223 4.3-20.599 8.525-68.941 18.688-80.325 10.162-11.384 16.34-18.645 31.816-12.852 15.476 5.793 23.96 17.196 31.003 27.97 7.043 10.775 9.862 44.266 17.196 44.266 7.334 0 34.078-2.884 34.078-9.691 0-6.808-24.525-94.835-47.9-101.053-23.374-6.218-94.496-3.584-102.25 42.107-7.755 45.69-13.075 74.521-23.08 83.355z" fill="#F68E00" />
                          <path d="M0 136.44c3.035 11.312 8.61 18.232 16.725 20.76 12.174 3.79 28.057 13.521 28.057-20.76 0-34.283 6.512-106.014 42.992-110.492 36.48-4.478 53.313 15.261 64.785 31.863 11.472 16.602 20.092 47.535 25.638 54.088 5.546 6.553 22.547 5.281 30.47 0 7.923-5.281-.68-25.956-10.193-43.374-9.513-17.418-34.355-74.873-98.66-67.95C35.508 7.5 23.039 55.359 16.725 75.226 12.516 88.467 6.941 108.872 0 136.438z" fill="#FF5ABB" />
                          <path d="M56.945 153.38c16.328.244 24.492-1.069 24.492-3.938 0-4.303-.463-50.522 10.306-60.025 10.77-9.503 24.236-19.51 33.66-8.708 9.424 10.802 9.756 50.169 15.115 50.169 5.358 0 26.01-3.08 26.01-14.117 0-11.038-27.458-58.853-41.125-62.11-13.667-3.255-24.63-7.446-43.966 4.178-8.163 4.906-19.717 18.835-24.492 35.758-6.536 23.17-4.793 50.256-4.793 51.94 0 1.944 1.598 4.228 4.793 6.852z" fill="#EEEA00" />
                        </g>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col col-right">
                <HtmlContent
                  className="about-para paragraph-bottom"
                  content={paragraphBottom.html}
                />
              </div>
            </div>
          </Wrapper>
        </section>
        <SliceZone
          allSlices={body}
          location={location}
        />
      </div>
    </Layout>
  );
};


export default About;

export const pageQuery = graphql`
  query AboutQuery {
    site {
      siteMetadata {
        blogSlug,
      }
    },
    etchedIceCream: file(relativePath: { eq: "assets/images/etched/ETCH_ICECREAM.PNG" }) {
      childImageSharp {
        fluid(maxWidth: 1000 quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    etchedRainbow: file(relativePath: { eq: "assets/images/etched/ETCH_RAINBOW.PNG" }) {
      childImageSharp {
        fluid(maxWidth: 1000 quality: 100) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    page: prismicAbout {
      uid,
      data {
        pageName: page_name {
          text
        }
        headline {
          text
        }
        paragraphTop: paragraph_top {
          html
        }
        paragraphBottom: paragraph_bottom {
          html
        }
        imageTopLeft: image_top_left {
          alt
          localFile {
            childImageSharp {
              fluid(maxWidth: 1200, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        imageRight: image_right {
          alt
          localFile {
            childImageSharp {
              fluid(maxWidth: 1200, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        imageBottomLeft: image_bottom_left {
          alt
          localFile {
            childImageSharp {
              fluid(maxWidth: 1200, quality: 90) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        metaTitle: meta_title {
          html
          text
        },
        metaDescription: meta_description {
          html
          text
        },
        openGraphImage: open_graph_image {
          alt
          copyright
          url
        }
        body {
          ... on PrismicAboutBodyTeamBlock {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
            }
            items {
              frameColour: frame_colour
              imageStyle: image_style
              name {
                text
              }
              photo {
                alt
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 90) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
              }
              position {
                text
              }
              shortBio: short_bio {
                html
              }
              speechBubbleText: speech_bubble_text {
                text
              }
            }
          }
          ... on PrismicAboutBodyClientsTicker {
            id
            sliceType: slice_type
            primary {
              title {
                text
              }
              sectionTheme: section_theme
            }
          }
          ... on PrismicAboutBodyEnquiryFormBlock {
            id
            sliceType: slice_type
            primary {
              sectionTheme: section_theme
              formTitle: form_title {
                text
              }
              formDescription: form_description {
                html
              }
            }
          }
        }
      }
    }
  }
`;
